/**
 * Profile Settings Page (Story 1.9)
 *
 * Allows users to view and edit their profile:
 * - Display name (editable)
 * - Email (read-only)
 * - Avatar (upload or Gravatar fallback)
 *
 * Features:
 * - Optimistic UI updates
 * - Toast notifications
 * - TanStack Query for data fetching
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
} from "@provenleap/ui";
import { apiGet, apiPatch } from "../../../lib/apiClient";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { AvatarUpload } from "../../../components/AvatarUpload";
import { PageSpinner } from "../../../components/PageSpinner";

export const Route = createFileRoute("/_authenticated/settings/profile")({
  component: ProfileSettingsPage,
});

interface ProfileData {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  emailVerified: boolean;
}

function ProfileSettingsPage() {
  const queryClient = useQueryClient();

  // Toast state (simple implementation)
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Clear toast after 3 seconds
  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch profile data
  const {
    data: profile,
    isLoading,
    error,
  } = useQuery<ProfileData>({
    queryKey: ["user", "profile"],
    queryFn: async () => {
      const response = await apiGet("/api/user/profile");
      if (!response.ok) {
        throw new Error("Failed to fetch profile");
      }
      const json = await response.json();
      return json.data;
    },
  });

  // Local state for display name input
  const [displayName, setDisplayName] = useState<string>("");
  const [hasChanges, setHasChanges] = useState(false);

  // Sync local state when profile loads
  if (profile && !hasChanges && displayName !== (profile.displayName ?? "")) {
    setDisplayName(profile.displayName ?? "");
  }

  // Update profile mutation
  const updateProfile = useMutation<
    unknown,
    Error,
    { displayName: string | null },
    { previous: ProfileData | undefined }
  >({
    mutationFn: async (data: { displayName: string | null }) => {
      const response = await apiPatch("/api/user/profile", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update profile");
      }
      return response.json();
    },
    onMutate: async (newData: { displayName: string | null }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["user", "profile"] });

      // Snapshot previous value
      const previous = queryClient.getQueryData<ProfileData>(["user", "profile"]);

      // Optimistic update
      queryClient.setQueryData<ProfileData>(["user", "profile"], (old: ProfileData | undefined) => {
        if (!old) return old;
        return {
          ...old,
          displayName: newData.displayName,
        };
      });

      return { previous };
    },
    onError: (err: Error, _newData: { displayName: string | null }, context: { previous: ProfileData | undefined } | undefined) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(["user", "profile"], context.previous);
      }
      showToast("error", err instanceof Error ? err.message : "Failed to update profile");
    },
    onSuccess: () => {
      showToast("success", "Profile updated");
      setHasChanges(false);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
    },
  });

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayName(e.target.value);
    setHasChanges(true);
  };

  const handleSave = () => {
    const trimmed = displayName.trim();
    updateProfile.mutate({
      displayName: trimmed.length > 0 ? trimmed : null,
    });
  };

  const handleAvatarUploadComplete = (newUrl: string) => {
    // Invalidate profile query to refresh
    queryClient.setQueryData<ProfileData>(["user", "profile"], (old: ProfileData | undefined) => {
      if (!old) return old;
      return { ...old, avatarUrl: newUrl };
    });
    queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
    showToast("success", "Avatar updated");
  };

  const handleAvatarError = (message: string) => {
    showToast("error", message);
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-[800px] mx-auto">
        <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Profile" }]} />
        <PageSpinner />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="p-6 max-w-[800px] mx-auto">
        <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Profile" }]} />
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive">Failed to load profile. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[800px] mx-auto">
      <h1 className="text-2xl font-bold mb-6">Profile Settings</h1>

      {/* Toast notification - accessible to screen readers */}
      {toast && (
        <div
          role="alert"
          aria-live="polite"
          className={`mb-4 p-3 rounded-md ${
            toast.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Avatar Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Profile Picture</CardTitle>
          <CardDescription>
            Upload a new avatar or use your Gravatar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AvatarUpload
            currentUrl={profile.avatarUrl}
            email={profile.email}
            displayName={profile.displayName}
            onUploadComplete={handleAvatarUploadComplete}
            onError={handleAvatarError}
          />
        </CardContent>
      </Card>

      {/* Profile Info Section */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>
            Update your profile information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email - Read Only */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Email Address
            </label>
            <Input
              type="email"
              value={profile.email}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Email cannot be changed
            </p>
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Display Name
            </label>
            <Input
              type="text"
              value={displayName}
              onChange={handleDisplayNameChange}
              placeholder="Enter your display name"
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              Your display name will be shown throughout the application
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateProfile.isPending}
            >
              {updateProfile.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
